import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { hasContent } from '@common/lit/hasContent.js';
import { Generation } from '@api-consent/types/common.js';
import { type TefComsConsentGroup } from '@src/components/tef-coms-consent-group.js';
import { filterPredicator } from '@src/components/templates/util.js';
import { cancelMicrofrontend } from '@src/store/consents.js';
import { store } from '@src/store/index.js';
import { ConsentGroup, ConsentType } from '@src/types/common.js';

export const clickTextTemplate = (
  checkBoxId: string,
  comsConsentGroup: TefComsConsentGroup,
) => {
  const { oneclick: isOneClick, name } = comsConsentGroup.consentGroup as ConsentGroup;

  let labelText = comsConsentGroup.consentGroup!.text;
  if (isOneClick && hasContent(comsConsentGroup.consentGroup!.oneclickScope)) {
    labelText = comsConsentGroup.consentGroup!.oneclickScope as string;
  }

  return html`
    <one-text padding="xs">
      <one-cluster y="start" gap="sm" no-wrap>
        <one-checkbox id=${checkBoxId} @change=${comsConsentGroup.oneclickAction.bind(comsConsentGroup)} data-testid="oneclick-checkbox-label-${name.toLowerCase()}"></one-checkbox>
        <one-text as="span" for=${checkBoxId}>
          ${unsafeHTML(labelText)}
        </one-text>
      </one-cluster>
    </one-text>
  `;
};

export const clickConsentsContainerTemplate = (
  stylecontext: string,
  comsConsentGroup: TefComsConsentGroup,
  collapsibleOpenGroupName: string,
) => {
  const { oneclickCollapsible } = comsConsentGroup.consentGroup as ConsentGroup;
  const summaryHtml = oneclickCollapsible
    ? unsafeHTML(oneclickCollapsible)
    : 'Wenn ich einzelne Kontaktkanäle und Verarbeitungsvorgänge von meiner Auswahl ausschließen möchte, kann ich hier gesonderte Einwilligungen erteilen.';

  return html`
    <one-accordion
      cloak
      context="${stylecontext}"
      ?is-open="${comsConsentGroup.groupName === collapsibleOpenGroupName}"
    >
      <one-accordion-item once>
        <div slot="summary">
          ${summaryHtml}
        </div>
        <one-list>
          <one-stack gap="md">
            <!-- consentPrefix -->
            ${comsConsentGroup.consentPrefix ? html`<one-text>${unsafeHTML(comsConsentGroup.consentPrefix)}</one-text>` : nothing}
            ${comsConsentGroup.groupConsents('group')}
          </one-stack>
        </one-list>
        </one-accordion-item>
      </one-accordion>
    `;
};

export const groupConsentsTemplate = (
  comsConsentGroup: TefComsConsentGroup,
) => {
  const { consents, required, displayMode } = comsConsentGroup.consentGroup as ConsentGroup;
  return html`
    <one-list>
      ${consents /* eslint-disable @typescript-eslint/indent */
        .filter(filterPredicator)
        .map(
          (consent) => html`
            ${consent.hintBefore ? html`<one-list-item class="coms-hint-before">${unsafeHTML(consent.hintBefore)}</one-list-item>` : nothing}
            <one-list-item class="coms-consent">
              <tef-coms-consent .consent=${consent} ?required=${required} .displayMode=${displayMode}></tef-coms-consent>
            </one-list-item>
            ${consent.hintAfter
              ? html`<one-list-item class="coms-hint-after">${unsafeHTML(consent.hintAfter)}</one-list-item>`
              : nothing}`,
        )/* eslint-enable @typescript-eslint/indent */}
    </one-list>
  `;
};

export const withdrawalTemplate = (
  comsConsentGroup: TefComsConsentGroup,
  checkboxId: string,
) => {
  const { denyall } = comsConsentGroup.consentGroup as ConsentGroup;
  return html`
        ${/* eslint-disable @typescript-eslint/indent */
        html`
          <one-box>
            <one-stack gap="xs">
              <one-checkbox
                id=${checkboxId}
                .checked=${comsConsentGroup.noConsentSet}
                @change=${comsConsentGroup.denyAction.bind(comsConsentGroup)}
              >
                <one-text as="span" for=${checkboxId}>
                  ${unsafeHTML(denyall!.toString())}
                </one-text>
              </one-checkbox>
            </one-stack>
          </one-box>
        `}
    `;
  };

export const groupViewTemplate = (
  comsConsentGroup: TefComsConsentGroup,
  consentGroupsView: ConsentGroup[],
  filteredConsents: ConsentType[],
  stylecontext: string,
) => {
  const {
      header, text, footer, denyallView, displayMode,
  } = consentGroupsView[comsConsentGroup.groupIndex];

  // Do we need a "Zurück" button?
  let backButtonHtml = html`${nothing}`;
  if (comsConsentGroup.mode.includes('buttons:cancel')) {
    backButtonHtml = html`
      <one-spacer size="fill"></one-spacer>
      <one-button @click=${() => { store.dispatch(cancelMicrofrontend()); }} variant="plain">Zurück</one-button>
    `;
  }

  /* eslint-disable sonarjs/slow-regex */
  return html`
    <one-stack gap="md">
      <one-content>
        <one-stack gap="lg">
          ${hasContent(header) ? html`<one-heading level="h4" as="span"><span>${header.replace(/<[^>]+>/g, '')}</span></one-heading>` : nothing}
          <one-text as="p">
            ${unsafeHTML(text)}
          </one-text>
          ${(filteredConsents.length === 0 && denyallView) ? html`
          <one-paper background="legal-2" padding="md" text-color="make-links-blau">${unsafeHTML(denyallView)}</one-paper>
            ` : html`
            <tef-coms-collapsible-group context="${stylecontext}">
              <one-stack gap="sm">
                ${filteredConsents.map((consent) => html`
                  <one-details variant="flipped">
                    <div slot="summary">${unsafeHTML(consent.shortInfo)}</div>
                    <!-- consentPrefix -->
                    ${comsConsentGroup.consentPrefix ? unsafeHTML(comsConsentGroup.consentPrefix) : nothing}
                    <tef-coms-consent .mode=${comsConsentGroup.mode} .consent=${consent} .displayMode=${displayMode}></tef-coms-consent>
                  </one-details>
                `)}
              </one-stack>
            </tef-coms-collapsible-group>
          `}
          ${footer ? html`
            <one-text as="p">
              ${unsafeHTML(footer)}
            </one-text>
          ` : nothing}
        </one-stack>
      </one-content>
      <one-button-group>
        <one-button @click=${comsConsentGroup.openGroupForEdit.bind(comsConsentGroup)} data-testid="button-update">Ändern</one-button>
        ${backButtonHtml}
      </one-button-group>
    </one-content>
  </one-stack>`;
  /* eslint-enable sonarjs/slow-regex */
};

export const groupTemplate = (comsConsentGroup: TefComsConsentGroup) => {
  const { header, footer, oneclick: isOneClick } = comsConsentGroup.consentGroup as ConsentGroup;
  const isAtLeastGenerationUnlock = comsConsentGroup.generation >= Generation.UNLOCK;

  /* eslint-disable sonarjs/slow-regex */
  return html`
    <one-content>
      <one-stack gap="sm">
        ${hasContent(header) ? html`<one-heading level="h4" as="span"><span>${header.replace(/<[^>]+>/g, '')}</span></one-heading>` : nothing}
        ${isOneClick && isAtLeastGenerationUnlock ? html`<one-text as="p">${comsConsentGroup.groupText}</one-text>` : nothing}
        <one-text as="p">${isOneClick ? comsConsentGroup.groupOneclickText : comsConsentGroup.groupText}</one-text>
        <!-- consentPrefix -->
        ${!isOneClick && comsConsentGroup.consentPrefix ? html`<one-text>${unsafeHTML(comsConsentGroup.consentPrefix)}</one-text>` : nothing}
        <one-stack gap="md">
          ${isOneClick ? comsConsentGroup.groupOneclickConsentsContainer : comsConsentGroup.groupConsents()}
        </one-stack>
        ${footer ? html`<one-text as="p">${unsafeHTML(footer)}</one-text>` : nothing}
        ${comsConsentGroup.withdrawal}
      </one-stack>
    </one-content>`;
  /* eslint-enable sonarjs/slow-regex */
};
